'use client';

import React, { useLayoutEffect, useState } from 'react';
import Image from 'next/image';
import { useAppBootLifecycle } from '@/contexts/AppBootLifecycleContext';

interface LedgerOpeningScreenProps {
  storageKey?: string;
  text?: string;
}

export function LedgerOpeningScreen({
  storageKey = 'ertzyx-ledger-opening-seen',
  text = 'OPENING YOUR LIVING LEDGER...',
}: LedgerOpeningScreenProps) {
  const bootLifecycle = useAppBootLifecycle();
  // 'pending' -- not 'hidden' -- is the initial state. React requires the server render and the
  // client's pre-effect render to match, so whatever this starts as is what the browser paints
  // immediately from the raw SSR HTML, before any JS has loaded or hydration has run (this is a
  // real HTTP round-trip after login's window.location.href navigation, not a client-side route
  // change). Starting 'hidden' meant that raw first paint was the bare dashboard underneath --
  // useLayoutEffect only avoids an extra frame of delay *after* hydration commits, it can't do
  // anything about a paint that already happened before hydration even started. That's the
  // actual "dashboard flashes, then the opening book appears" bug: not a race inside this
  // component's own re-render, but the SSR-to-hydration gap itself. Starting 'pending' instead
  // means the browser's first paint is always this neutral cover, never the bare dashboard --
  // the layout effect below then resolves it to the real animation or straight to hidden before
  // the next paint, depending on sessionStorage (unreadable server-side, so it can only be
  // consulted once we're actually running on the client).
  const [phase, setPhase] = useState<'pending' | 'hidden' | 'open' | 'leaving'>('pending');

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let shouldPlay = false;

    if (bootLifecycle) {
      shouldPlay = bootLifecycle.claimExperience(storageKey);
    } else {
      // Keep the component independently usable in isolated surfaces/tests. The
      // root application provider is the production authority because it also
      // survives route-key remounts and Web Storage failures.
      try {
        shouldPlay = window.sessionStorage.getItem(storageKey) !== 'true';
        if (shouldPlay) window.sessionStorage.setItem(storageKey, 'true');
      } catch {
        shouldPlay = true;
      }
    }

    if (!shouldPlay) {
      // Already seen this tab session (e.g. a page refresh) -- resolve straight to hidden.
      // Never replay the animation just because the SSR-safe default above had to guess.
      setPhase('hidden');
      return;
    }

    setPhase('open');

    const hold = window.setTimeout(() => setPhase('leaving'), reduceMotion ? 400 : 3400);
    const remove = window.setTimeout(() => setPhase('hidden'), reduceMotion ? 700 : 4100);

    return () => {
      window.clearTimeout(hold);
      window.clearTimeout(remove);
    };
  }, [bootLifecycle, storageKey]);

  if (phase === 'hidden') return null;

  if (phase === 'pending') {
    return (
      <div
        data-testid="ledger-opening-cover"
        className="fixed inset-0 z-[80] flex items-center justify-center bg-[#060d1a]"
        role="status"
        aria-live="polite"
        aria-label="Loading"
      >
        <Image
          src="/brand/ertzyx-logo.png"
          alt="Ertzyx"
          width={296}
          height={296}
          sizes="120px"
          className="h-auto w-[120px] object-contain opacity-90"
          priority
        />
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-0 z-[80] flex items-center justify-center overflow-hidden bg-[#060d1a] transition-opacity duration-700 ${
        phase === 'leaving' ? 'opacity-0' : 'opacity-100'
      }`}
      role="status"
      aria-live="polite"
    >
      {/* Deep background gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_70%_at_50%_50%,rgba(0,180,160,0.10)_0%,transparent_55%),radial-gradient(ellipse_70%_50%_at_50%_58%,rgba(201,168,76,0.12)_0%,transparent_55%),linear-gradient(180deg,#020810_0%,#060d1a_50%,#030a14_100%)]" />

      {/* Top gold rule */}
      <div className="absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent_5%,#C9A84C55_30%,#DCBE6E_50%,#C9A84C55_70%,transparent_95%)]" />
      {/* Bottom gold rule */}
      <div className="absolute inset-x-0 bottom-0 h-px bg-[linear-gradient(90deg,transparent_5%,#C9A84C33_30%,#C9A84C55_50%,#C9A84C33_70%,transparent_95%)]" />

      {/* Ambient teal glow left */}
      <div className="absolute left-[28%] top-1/2 h-[32rem] w-[32rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00b4a0]/12 blur-[100px]" />
      {/* Ambient gold glow right */}
      <div className="absolute left-[72%] top-1/2 h-[32rem] w-[32rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C9A84C]/12 blur-[100px]" />
      {/* Center warmth glow */}
      <div className="absolute left-1/2 top-1/2 h-[20rem] w-[20rem] -translate-x-1/2 -translate-y-[55%] rounded-full bg-[#C9A84C]/8 blur-[70px]" />

      <div className="relative flex flex-col items-center text-center">
        {/* Book stage */}
        <div className="ledger-stage relative mb-8 h-[17rem] w-[30rem] max-w-[90vw] sm:h-[20rem] sm:w-[38rem] lg:h-[22rem] lg:w-[46rem]">
          {/* Ground shadow */}
          <div className="absolute inset-x-[10%] bottom-[-18px] h-6 rounded-full bg-black/60 blur-2xl" />

          {/* LEFT PAGE - Teal */}
          <div className="ledger-page ledger-page-left absolute left-0 top-0 h-full w-1/2 overflow-hidden rounded-tl-[22px] rounded-bl-[22px] border-t border-b border-l border-[#00b4a0]/45 shadow-[-4px_0_48px_rgba(0,180,160,0.22)]">
            {/* Page background */}
            <div className="absolute inset-0 bg-[linear-gradient(145deg,#0d3d38_0%,#0a2e2a_45%,#061c19_100%)]" />
            {/* Subtle page texture overlay */}
            <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_18px,rgba(0,180,160,0.04)_18px,rgba(0,180,160,0.04)_19px)]" />
            {/* Teal shimmer at top edge */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[linear-gradient(90deg,transparent,#00b4a0,transparent_80%)]" />
            {/* Inner frame border */}
            <div className="absolute inset-[10px] border border-[#00b4a0]/22 rounded-tl-[14px] rounded-bl-[14px]" />
            {/* Column/pillar decoration echoing the logo */}
            <div className="absolute left-6 top-6 bottom-6 flex items-start gap-[5px]">
              <div className="h-full w-[3px] rounded-full bg-[linear-gradient(180deg,#00b4a0cc,#00b4a0,#00b4a099)] shadow-[0_0_14px_rgba(0,180,160,0.6)]" />
              <div className="mt-3 h-[78%] w-[2px] rounded-full bg-[#00b4a0]/40" />
              <div className="mt-5 h-[62%] w-[2px] rounded-full bg-[#00b4a0]/25" />
            </div>
            {/* Ruled ledger lines */}
            <div className="absolute left-16 right-5 top-7 flex flex-col gap-[16px]">
              {[0.5, 0.4, 0.3, 0.25, 0.2, 0.18, 0.15, 0.12].map((op, i) => (
                <div key={i} style={{ opacity: op }} className="h-px bg-[#00b4a0]" />
              ))}
            </div>
            {/* Teal corner glow */}
            <div className="absolute -bottom-10 -right-10 h-44 w-44 rounded-full bg-[#00b4a0]/18 blur-3xl" />
            <div className="absolute -top-6 left-10 h-24 w-24 rounded-full bg-[#00b4a0]/12 blur-2xl" />
            {/* Bottom corner seal dot */}
            <div className="absolute bottom-5 left-6 flex h-9 w-9 items-center justify-center rounded-full border border-[#00b4a0]/45 bg-[#00b4a0]/8">
              <div className="h-[10px] w-[10px] rounded-full bg-[#00b4a0]/70 shadow-[0_0_8px_rgba(0,180,160,0.8)]" />
            </div>
            {/* Page number area */}
            <div className="absolute bottom-[22px] right-5 text-[9px] font-bold tracking-[0.2em] text-[#00b4a0]/45 uppercase">
              I
            </div>
          </div>

          {/* RIGHT PAGE - Gold */}
          <div className="ledger-page ledger-page-right absolute right-0 top-0 h-full w-1/2 overflow-hidden rounded-tr-[22px] rounded-br-[22px] border-t border-b border-r border-[#C9A84C]/45 shadow-[4px_0_48px_rgba(201,168,76,0.22)]">
            {/* Page background */}
            <div className="absolute inset-0 bg-[linear-gradient(215deg,#2c1d05_0%,#1f1504_45%,#110d03_100%)]" />
            {/* Subtle page texture overlay */}
            <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,transparent,transparent_18px,rgba(201,168,76,0.04)_18px,rgba(201,168,76,0.04)_19px)]" />
            {/* Gold shimmer at top edge */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[linear-gradient(90deg,transparent,#C9A84C,transparent_80%)]" />
            {/* Inner frame border */}
            <div className="absolute inset-[10px] border border-[#C9A84C]/22 rounded-tr-[14px] rounded-br-[14px]" />
            {/* Concentric arc decoration (fingerprint/scroll motif from logo) */}
            <svg
              className="absolute right-4 top-4 h-32 w-32 opacity-30"
              viewBox="0 0 128 128"
              fill="none"
            >
              {[12, 22, 32, 42, 52, 62].map((r, i) => (
                <path
                  key={i}
                  d={`M ${64},${64 - r} A ${r},${r} 0 0 0 ${64 - r},64`}
                  stroke="#C9A84C"
                  strokeWidth={i === 0 ? 2 : 1.5}
                  strokeLinecap="round"
                  opacity={1 - i * 0.12}
                />
              ))}
            </svg>
            {/* Ruled ledger lines */}
            <div className="absolute left-5 right-14 top-7 flex flex-col gap-[16px]">
              {[0.5, 0.4, 0.3, 0.25, 0.2, 0.18, 0.15, 0.12].map((op, i) => (
                <div key={i} style={{ opacity: op }} className="h-px bg-[#C9A84C]" />
              ))}
            </div>
            {/* Gold corner glow */}
            <div className="absolute -bottom-10 -left-10 h-44 w-44 rounded-full bg-[#C9A84C]/16 blur-3xl" />
            <div className="absolute -top-6 right-10 h-24 w-24 rounded-full bg-[#C9A84C]/12 blur-2xl" />
            {/* Page curl hint */}
            <div className="absolute bottom-0 right-0 h-12 w-12 bg-[linear-gradient(225deg,rgba(201,168,76,0.18)_0%,transparent_60%)] rounded-tl-full" />
            {/* Bottom corner seal dot */}
            <div className="absolute bottom-5 right-6 flex h-9 w-9 items-center justify-center rounded-full border border-[#C9A84C]/45 bg-[#C9A84C]/8">
              <div className="h-[10px] w-[10px] rounded-full bg-[#C9A84C]/70 shadow-[0_0_8px_rgba(201,168,76,0.8)]" />
            </div>
            {/* Page number area */}
            <div className="absolute bottom-[22px] left-5 text-[9px] font-bold tracking-[0.2em] text-[#C9A84C]/45 uppercase">
              II
            </div>
          </div>

          {/* Central spine glow */}
          <div className="absolute inset-y-0 left-1/2 z-10 w-[2px] -translate-x-1/2 bg-[linear-gradient(180deg,rgba(242,216,137,0.18)_0%,rgba(220,190,110,0.62)_20%,rgba(242,216,137,0.76)_50%,rgba(220,190,110,0.62)_80%,rgba(242,216,137,0.18)_100%)] shadow-[0_0_24px_rgba(242,216,137,0.72),0_0_54px_rgba(201,168,76,0.36),0_0_90px_rgba(201,168,76,0.18)]" />

          {/* Central logo seal */}
          <div className="logo-seal absolute inset-0 z-50 flex items-center justify-center">
            <div className="relative flex min-w-[156px] flex-col items-center rounded-[18px] border border-[#DCBE6E]/70 bg-[#060d1a]/96 px-7 py-5 shadow-[0_0_54px_rgba(0,180,160,0.22),0_0_46px_rgba(201,168,76,0.2),0_0_90px_rgba(201,168,76,0.1),inset_0_0_24px_rgba(220,190,110,0.05)] sm:min-w-[174px] sm:px-8 sm:py-6">
              {/* Inner gold corner accents */}
              <div className="absolute left-[10px] top-[10px] h-5 w-5 border-l-2 border-t-2 border-[#DCBE6E]/60 rounded-tl-[6px]" />
              <div className="absolute right-[10px] top-[10px] h-5 w-5 border-r-2 border-t-2 border-[#DCBE6E]/60 rounded-tr-[6px]" />
              <div className="absolute bottom-[10px] left-[10px] h-5 w-5 border-b-2 border-l-2 border-[#DCBE6E]/60 rounded-bl-[6px]" />
              <div className="absolute bottom-[10px] right-[10px] h-5 w-5 border-b-2 border-r-2 border-[#DCBE6E]/60 rounded-br-[6px]" />
              {/* Logo */}
              <Image
                src="/brand/ertzyx-logo.png"
                alt="Ertzyx"
                width={296}
                height={296}
                sizes="(min-width: 640px) 148px, 136px"
                className="ledger-logo h-auto w-[136px] object-contain drop-shadow-[0_0_10px_rgba(201,168,76,0.34)] sm:w-[148px]"
                priority
              />
              {/* Gold divider */}
              <div className="mt-3 h-px w-28 bg-[linear-gradient(90deg,transparent,#C9A84C,#DCBE6E,#C9A84C,transparent)]" />
            </div>
          </div>
        </div>

        {/* Label */}
        <p className="label-fade text-[10px] font-black uppercase tracking-[0.30em] text-[#f2d889] drop-shadow-[0_0_12px_rgba(242,216,137,0.5)] sm:text-[11px]">
          {text}
        </p>

        {/* Subtle dot row below label */}
        <div className="dot-fade mt-4 flex gap-[6px]">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-[5px] w-[5px] rounded-full bg-[#C9A84C]/60"
              style={{ animationDelay: `${1400 + i * 180}ms` }}
            />
          ))}
        </div>
      </div>

      <style jsx>{`
        .ledger-stage {
          perspective: 1600px;
          transform-style: preserve-3d;
        }
        .ledger-page {
          backface-visibility: hidden;
          transform-style: preserve-3d;
          will-change: transform;
          z-index: 20;
        }
        .ledger-page-left {
          transform-origin: right center;
          animation: openLedgerLeft 1800ms cubic-bezier(0.16, 1, 0.3, 1) 250ms both;
        }
        .ledger-page-right {
          transform-origin: left center;
          animation: openLedgerRight 1800ms cubic-bezier(0.16, 1, 0.3, 1) 250ms both;
        }
        .logo-seal {
          transform: translate3d(0, 0, 140px);
          transform-style: preserve-3d;
          will-change: transform, opacity;
          animation: sealRise 1000ms cubic-bezier(0.16, 1, 0.3, 1) 900ms both;
        }
        .ledger-logo {
          transform: translateZ(0);
          image-rendering: auto;
        }
        .label-fade {
          animation: labelFade 900ms ease-out 1300ms both;
        }
        .dot-fade > div {
          animation: dotPulse 1000ms ease-out both;
        }
        @keyframes openLedgerLeft {
          0% {
            transform: rotateY(0deg) translateZ(0);
            filter: brightness(0.5);
          }
          20% {
            filter: brightness(0.85);
          }
          100% {
            transform: rotateY(-76deg) translateX(-42px) translateZ(18px);
            filter: brightness(1);
          }
        }
        @keyframes openLedgerRight {
          0% {
            transform: rotateY(0deg) translateZ(0);
            filter: brightness(0.5);
          }
          20% {
            filter: brightness(0.85);
          }
          100% {
            transform: rotateY(76deg) translateX(42px) translateZ(18px);
            filter: brightness(1);
          }
        }
        @keyframes sealRise {
          0% {
            transform: translate3d(0, 20px, 140px) scale(0.94);
            opacity: 0;
          }
          100% {
            transform: translate3d(0, 0, 140px) scale(1);
            opacity: 1;
          }
        }
        @keyframes labelFade {
          0% {
            opacity: 0;
            transform: translateY(8px);
            letter-spacing: 0.18em;
          }
          100% {
            opacity: 1;
            transform: translateY(0);
            letter-spacing: 0.3em;
          }
        }
        @keyframes dotPulse {
          0% {
            opacity: 0;
            transform: scale(0.5);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .ledger-page,
          .logo-seal,
          .label-fade,
          .dot-fade > div {
            animation: none;
            opacity: 1;
          }
          .ledger-page-left {
            transform: rotateY(-76deg) translateX(-42px) translateZ(18px);
          }
          .ledger-page-right {
            transform: rotateY(76deg) translateX(42px) translateZ(18px);
          }
        }
      `}</style>
    </div>
  );
}
